import type { ReactNode } from 'react'
import { useTranslation } from '../localization/useTranslation'

/** The send-in artefact fields we render — a structural subset of Core's `ShippingLabel`. */
export interface SendInLabel {
  labelFailed?: boolean | null
  labelCode?: string | null
  qrUrl?: string | null
  url?: string | null
}

interface SendInInstructionsProps {
  label: SendInLabel | null | undefined
  /** In-store drop-off / home pickup: no shipping artefact to show. */
  isPickup: boolean
  /** Rendered when no artefact is available yet (Confirm shows a spinner; history shows a note/nothing). */
  whenPending?: ReactNode
}

/**
 * How the customer sends in their parcel, rendered from the async-generated label — one of the ported
 * v1 cases: a generation failure, a drop-off **code**, a **QR** link, or a printable **label** link.
 * Shared by the Confirm step and the "My trade-ins" detail dialog so the logic lives in one place.
 * Returns `null` for pickup (nothing to send in) and `whenPending` while no artefact exists yet.
 */
export function SendInInstructions({ label, isPickup, whenPending = null }: SendInInstructionsProps) {
  const { t } = useTranslation()
  if (isPickup) return null

  const openUrl = (url?: string | null) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  if (label?.labelFailed) {
    return <div className="codebox codebox--warn">{t('confirm.labelFailed')}</div>
  }
  if (label?.labelCode) {
    return (
      <div className="codebox">
        <span className="k">{t('confirm.shippingCode')}</span>
        <span className="code">{label.labelCode}</span>
      </div>
    )
  }
  if (label?.qrUrl) {
    return (
      <button type="button" className="btn btn-primary btn-sm" onClick={() => openUrl(label.qrUrl)}>
        {t('confirm.showQr')}
      </button>
    )
  }
  if (label?.url) {
    return (
      <button type="button" className="btn btn-primary btn-sm" onClick={() => openUrl(label.url)}>
        {t('confirm.showLabel')}
      </button>
    )
  }
  return <>{whenPending}</>
}
